import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import yaml from 'js-yaml';

import { type RootState } from '@editor2/reducers/root';
import { type SelectOption } from './types';

function parseChapterLabel(yamlConfig: string): string {
	try {
		const parsed = yaml.load(yamlConfig) as { [key: string]: unknown } | null;
		const chapters = parsed?.chapters as { [key: string]: unknown } | undefined;
		const humanName = chapters?.human_name;
		if (typeof humanName === 'string' && humanName.length > 0) {
			return humanName.charAt(0).toUpperCase() + humanName.slice(1);
		}
	} catch {
		// Fall through to default
	}
	return 'Chapter';
}

export const useSelectOptions = (courseId: number): SelectOption[] => {
	const { chapters, pages, page_groups, book } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	return useMemo<SelectOption[]>(() => {
		const chapterLabel = parseChapterLabel(book.yaml_config);
		const options: SelectOption[] = [];
		const includedPageIds = new Set<string | number>();

		function pushPageOption(pageId: string | number): void {
			if (!pages) return;
			const page = pages.entities[pageId];
			if (!page) return;
			includedPageIds.add(pageId);
			const num = page.page_number?.trim();
			const hasNumber = num != null && num !== '';
			const prefix = hasNumber ? `Page ${num} ` : '';
			options.push({
				value: page.family_id ?? String(page.id ?? pageId),
				label: `${prefix}${page.name}`,
				type: 'page',
				pageType: page.page_type
			});
		}

		if (chapters) {
			chapters.ids.forEach((chapterId) => {
				const chapter = chapters.entities[chapterId];
				if (!chapter) return;

				// Push the chapter option
				const num = chapter.chapter_number?.trim();
				const prefix = num ? `${chapterLabel} ${num}: ` : `${chapterLabel}: `;
				options.push({
					value: chapter.family_id ?? String(chapter.id ?? chapterId),
					label: `${prefix}${chapter.name}`,
					type: 'chapter'
				});

				// Push this chapter's pages immediately after
				if (chapter.page_groups_enabled && chapter.page_groups) {
					chapter.page_groups.forEach((pgId) => {
						const pageGroup = page_groups?.entities[pgId];
						if (pageGroup?.pages) {
							pageGroup.pages.forEach(pushPageOption);
						}
					});
				} else if (chapter.pages) {
					chapter.pages.forEach(pushPageOption);
				}
			});
		}

		// Append any pages not associated with a chapter
		if (pages) {
			pages.ids.forEach((id) => {
				if (!includedPageIds.has(id)) {
					pushPageOption(id);
				}
			});
		}

		return options;
	}, [chapters, pages, page_groups, book.yaml_config]);
};
